import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Badge, Button, Col, Container, Row, Table } from 'reactstrap';
import ArrowUpIcon from 'mdi-react/ArrowUpIcon';
import ArrowDownIcon from 'mdi-react/ArrowDownIcon';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import CheckIcon from 'mdi-react/CheckIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';
import FormCard from '../../../shared/components/custom/FormCard';
import useModal from '../../../shared/hooks/useModal';
import Api from '../../../util/api';
import DeleteModal from '../../../shared/components/custom/DeleteModal';
import Pagination from '../../../shared/components/pagination/Pagination';
import { partial } from '../../../util/translation/translation';
import { formatDate } from '../../../util/date-and-time';
import { formatCurrencyNumber } from '../../../util/functions';
import useConfig from '../../../util/useConfig';

const SCORE_TYPE_LABELS = {
  goal: 'scoreTypeGoal',
  point: 'scoreTypePoint',
  attendant: 'scoreTypeAttendant',
  holeInOne: 'scoreTypeHoleInOne',
  medal: 'scoreTypeMedal',
};

const GoalSponsorPlanList = ({ setLoading }) => {
  const config = useConfig();
  const history = useHistory();
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [handleModal, visible, deleteID] = useModal();
  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 1,
    perPage: 10,
  });
  const [order, setOrder] = useState({
    orderBy: 'createdAt',
    desc: true,
  });
  const p = partial('goalSponsor');
  const s = partial('shared');

  const fetchSubscriptionPlans = async (
    currentPage = 1,
    pageSize = 10,
    orderObject = {
      orderBy: 'createdAt',
      desc: true,
    }
  ) => {
    setLoading(true);
    try {
      const resSubscriptionPlans =
        await Api.goalSponsor.subscriptionPlans.getSubscriptionPlans(
          { page: currentPage, take: pageSize },
          {
            orderBy: orderObject.orderBy,
            direction: orderObject.desc ? 'DESC' : 'ASC',
          }
        );
      setSubscriptionPlans(resSubscriptionPlans.data);
      setPagination((old) => ({ ...old, total: resSubscriptionPlans.count }));
      setLoading(false);
    } catch (err) {
      toast.error(p('subscriptionPlansRetrievingERROR'));
      setLoading(false);
    }
    setHasFetched(true);
  };
  useEffect(() => {
    fetchSubscriptionPlans();
  }, []);
  const deleteSubscriptionPlan = async () => {
    setLoading(true);
    try {
      await Api.goalSponsor.subscriptionPlans.deleteSubscriptionPlan(deleteID);
      toast.success(`${p('subscriptionPlanDeleted')}`);
    } catch (err) {
      toast.error(p('subscriptionPlanDeletedERROR'));
    } finally {
      handleModal();
      fetchSubscriptionPlans(pagination.currentPage, pagination.perPage, order);
    }
  };

  const onChangePage = (pager) => {
    if (
      pager.pageSize !== pagination.perPage ||
      pager.currentPage !== pagination.currentPage
    ) {
      setPagination((old) => ({
        ...old,
        currentPage: pager.currentPage,
        perPage: pager.pageSize,
      }));
      fetchSubscriptionPlans(pager.currentPage, pager.pageSize, order);
    }
  };

  const renderSort = (cellOrder) => {
    if (order.orderBy !== cellOrder) {
      return undefined;
    }
    return order.desc ? <ArrowDownIcon size={16} /> : <ArrowUpIcon size={16} />;
  };
  const handleSort = (incommingOrder) => {
    const newOrder = {
      orderBy: incommingOrder,
      desc: incommingOrder === order.orderBy ? !order.desc : true,
    };
    setOrder(newOrder);
    setPagination({
      total: 0,
      currentPage: 1,
      perPage: 10,
    });
    fetchSubscriptionPlans(1, 10, newOrder);
  };

  const renderSubscriptionPlans = () => {
    return subscriptionPlans.map((subscriptionPlan, i) => {
      const sumValue = subscriptionPlan.paymentMetadata.sum / 100 || 0;
      const targetValue = subscriptionPlan.planDesiredValue || 0;
      const targetPercentage =
        targetValue > 0 ? Math.round((sumValue / targetValue) * 100) : null;
      const scoreLabel = p(
        SCORE_TYPE_LABELS[subscriptionPlan.scoreType] || SCORE_TYPE_LABELS.goal
      );
      return (
        <tr
          style={{ background: i % 2 ? '#fff' : '#F9FAFB' }}
          key={`subscription-plan-${subscriptionPlan.id}`}
        >
          <td>
            <div className="flex-Vcenter">
              <Link
                to={`/goal-sponsor/subscription-plans/edit/${subscriptionPlan.id}`}
              >
                {subscriptionPlan.name}
              </Link>
            </div>
            <div
              className="marginTop-5"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px 6px',
              }}
            >
              <Badge
                pill
                color={subscriptionPlan.isActive ? 'success' : 'secondary'}
              >
                {subscriptionPlan.isActive ? (
                  <CheckIcon size={12} />
                ) : (
                  <CloseIcon size={12} />
                )}{' '}
                {subscriptionPlan.isActive ? s('active') : s('inactive')}
              </Badge>
              <Badge pill color="info">
                {scoreLabel}
              </Badge>
              {subscriptionPlan.externalInvoiceCompanies && (
                <Badge pill color="success">
                  {p('invoiceEnabled')}
                </Badge>
              )}
            </div>
          </td>
          <td>
            {formatDate(subscriptionPlan.createdAt)} –{' '}
            {formatDate(subscriptionPlan.endDate)}
          </td>
          <td>{subscriptionPlan.userCount || 0}</td>
          <td>
            <div>
              <strong style={{ fontSize: '1.05rem' }}>
                {formatCurrencyNumber(sumValue)} {config.currencyCaps}
              </strong>
            </div>
            <small className="text-muted">
              {subscriptionPlan.paymentMetadata.gameCount || 0} {p('gameCount')}{' '}
              &middot; {subscriptionPlan.paymentMetadata.goalCount || 0}{' '}
              {p('scoreCountLabel', { score: scoreLabel })}
            </small>
            {subscriptionPlan.showPriceMeter && (
              <div>
                <small className="text-muted">
                  {p('target')}: {formatCurrencyNumber(targetValue)}{' '}
                  {config.currencyCaps}
                  {targetPercentage !== null ? ` (${targetPercentage}%)` : ''}
                </small>
              </div>
            )}
          </td>
          <td>
            <div className="icon-actions">
              <SquareIconButton
                icon={<EditOutlineIcon />}
                onClick={() =>
                  history.push(
                    `/goal-sponsor/subscription-plans/edit/${subscriptionPlan.id}`
                  )
                }
                title={s('edit')}
                id={`edit-subscription-plan-${subscriptionPlan.id}`}
              />
              <SquareIconButton
                icon={<DeleteOutlineIcon />}
                onClick={() => handleModal(subscriptionPlan.id)}
                title={s('delete')}
                tone="danger"
                id={`delete-subscription-plan-${subscriptionPlan.id}`}
              />
            </div>
          </td>
        </tr>
      );
    });
  };
  return (
    <Container>
      <DeleteModal
        visible={visible}
        type={p('subscriptionPlan')}
        handleModal={handleModal}
        modalAction={deleteSubscriptionPlan}
      />
      <Row>
        <Col>
          <FormCard
            title={p('subscriptionPlans')}
            actions={
              <Link to="/goal-sponsor/subscription-plans/create">
                <Button size="sm" color="primary" className="mb-0">
                  {s('create')}
                </Button>
              </Link>
            }
            bodyClassName="form-card__body--flush"
            className="mb-3"
          >
            <Table responsive striped className="table--roomy">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('name')}>
                    <div className="flex alignItems-center">
                      {s('name')} {renderSort('name')}
                    </div>
                  </th>
                  <th
                    className="sortable"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex alignItems-center">
                      {s('period')} {renderSort('createdAt')}
                    </div>
                  </th>
                  <th
                    className="sortable"
                    onClick={() => handleSort('userCount')}
                  >
                    <div className="flex alignItems-center">
                      {p('subscriptionCount')} {renderSort('userCount')}
                    </div>
                  </th>
                  <th>
                    <div className="flex alignItems-center">{p('sum')}</div>
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {hasFetched && subscriptionPlans.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-muted">
                      {p('noFundraisers')}
                    </td>
                  </tr>
                ) : (
                  renderSubscriptionPlans()
                )}
              </tbody>
            </Table>
          </FormCard>
          <Pagination
            items={pagination.total}
            onChangePage={onChangePage}
            initialPage={pagination.currentPage}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default GoalSponsorPlanList;
